import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import axios from "axios";

function CheckOut() {
  const navigate = useNavigate();
  const { cartItems, designCart, total, clearCart, clearDesignCart } =
    useCart();
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const shippingCost = 5.0;
  const discount = 0.0;

  // 🧩 Combined all items (cookie cart + localStorage design cart)
  const [allItems, setAllItems] = useState([]);

  useEffect(() => {
    const combined = [...cartItems, ...designCart];
    setAllItems(combined);
  }, [cartItems, designCart]);

  const cartTotal = allItems.reduce(
    (acc, item) => acc + (item.price || 0) * (item.quantity || 1),
    0
  );
  const finalTotal = cartTotal + shippingCost - discount;

  // 🧾 Form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    city: "",
    state: "",
    postalCode: "",
    message: "",
    cardType: "MASTERCARD",
    cardNumber: "",
    expirationDate: "",
    cvv: "",
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://via.placeholder.com/150x150?text=No+Image";
    if (imagePath.startsWith("data:image")) return imagePath;
    if (imagePath.startsWith("/uploads")) return imagePath;
    if (imagePath.startsWith("http")) return imagePath;
    return "https://via.placeholder.com/150x150?text=Invalid+Image";
  };

  const handleConfirmOrder = async () => {
    if (allItems.length === 0) {
      alert("Your cart is empty!");
      return;
    }

    // Basic validation
    if (
      !formData.firstName ||
      !formData.email ||
      !formData.phone ||
      !formData.address
    ) {
      alert("Please fill in all required fields!");
      return;
    }

    // Prepare order data
    const orderData = {
      items: allItems.map((item) => ({
        product: item.id || item._id,
        title: item.name || item.title,
        category: item.categoryId || item.category || "CustomDesign",
        quantity: item.quantity || 1,
        price: item.price || 0,
        image:
          item.image ||
          item.customDesign?.frontImage ||
          item.customDesign?.backImage ||
          "",
        isDesign: !!item.customDesign, // mark if it's a custom design
      })),
      subtotal: cartTotal,
      discount: discount,
      shipping: shippingCost,
      total: finalTotal,
      status: "Pending",
    };

    try {
      setLoading(true);
      const response = await axios.post("/api/order/create", orderData);

      if (response.data.success) {
        clearCart();
        clearDesignCart();
        alert("Order placed successfully!");
        navigate("/");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white text-gray-900 min-h-screen px-6 py-12 font-outfit">
      <div className="container mx-auto grid lg:grid-cols-3 gap-10">
        {/* LEFT SECTION */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-[#6cc5bf] mb-6 font-syne">
              Fill Out The Details
            </h2>
            <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300 focus:outline-none focus:border-[#6cc5bf]"
                placeholder="Enter Your First Name"
              />
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your Last Name"
              />
              <input
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your Email Address"
              />
              <input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your Phone Number"
              />
              <input
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="col-span-2 p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your Shipping Address"
              />
              <input
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your Country"
              />
              <input
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your City"
              />
              <input
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your State"
              />
              <input
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                className="p-3 rounded-full bg-white border border-gray-300"
                placeholder="Enter Your Postal Code"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="col-span-2 p-3 rounded-xl bg-white border border-gray-300 h-28 resize-none"
                placeholder="Enter Your Message"
              ></textarea>
            </form>
          </div>

          {/* PAYMENT INFO */}
          <div>
            <h2 className="text-2xl font-bold text-[#6cc5bf] mb-6 font-syne">
              Payment Information
            </h2>
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setPaymentMethod("card")}
                className={`px-6 py-2 rounded-full font-syne ${
                  paymentMethod === "card"
                    ? "bg-[#6cc5bf] text-white"
                    : "bg-gray-200 text-black"
                }`}
              >
                Card
              </button>
              <button
                onClick={() => setPaymentMethod("cod")}
                className={`px-6 py-2 rounded-full font-syne ${
                  paymentMethod === "cod"
                    ? "bg-[#6cc5bf] text-white"
                    : "bg-gray-200 text-black"
                }`}
              >
                Cash On Delivery
              </button>
            </div>

            {paymentMethod === "card" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <select
                  name="cardType"
                  value={formData.cardType}
                  onChange={handleInputChange}
                  className="p-3 rounded-full bg-white border border-gray-300"
                >
                  <option>MASTERCARD</option>
                  <option>VISA</option>
                  <option>AMEX</option>
                </select>
                <input
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  className="p-3 rounded-full bg-white border border-gray-300"
                  placeholder="Enter Your Card Number"
                />
                <input
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleInputChange}
                  className="p-3 rounded-full bg-white border border-gray-300"
                  placeholder="Expiration Date (MM/YY)"
                />
                <input
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                  className="p-3 rounded-full bg-white border border-gray-300"
                  placeholder="CVV/CVC"
                />
              </div>
            )}

            <div className="mt-8">
              <button
                onClick={handleConfirmOrder}
                disabled={loading}
                className="px-8 py-3 bg-[#6cc5bf] text-white rounded-full font-bold font-syne hover:bg-[#5ab4ae] transition-colors disabled:opacity-50"
              >
                {loading ? "Processing..." : "Confirm Order"}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION - ORDER SUMMARY */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 space-y-6">
          <h2 className="text-2xl font-bold text-[#6cc5bf] mb-6 font-syne">
            Order Summary
          </h2>

          <div className="space-y-4">
            {allItems.length === 0 ? (
              <p className="text-gray-500 text-center">Your cart is empty</p>
            ) : (
              allItems.map((item) => (
                <div
                  key={item.id || item._id}
                  className="flex items-center gap-4"
                >
                  <img
                    src={getImageUrl(
                      item.image ||
                        item.customDesign?.frontImage ||
                        item.customDesign?.backImage
                    )}
                    alt={item.name}
                    className="w-16 h-16 rounded-lg object-cover border"
                  />
                  <div className="flex-1">
                    <p className="font-semibold font-outfit">
                      {item.name || item.title || "Unnamed"}
                    </p>
                    <span className="text-sm text-gray-500">
                      ${item.price?.toFixed(2)} x {item.quantity || 1}
                    </span>
                  </div>
                  <span className="font-bold text-[#6cc5bf]">
                    ${(item.price * (item.quantity || 1)).toFixed(2)}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Totals */}
          <div className="text-gray-700 space-y-2 text-sm font-outfit">
            <div className="flex justify-between">
              <span>Price:</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-outfit text-lg pt-2 border-b border-gray-300 pb-2">
              <span>Discount:</span>
              <span>${discount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping:</span>
              <span>${shippingCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-gray-900 font-outfit text-lg pt-2 border-b border-gray-300 pb-2">
              <span>Total:</span>
              <span className="text-[#6cc5bf]">${finalTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>ETA:</span>
              <span>5–7 Business Days</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CheckOut;
